import { Link } from "react-router-dom";
import { ArrowRight, Globe, Megaphone, Workflow, Briefcase, MapPin } from "lucide-react";
import { LeadForm } from "@/components/landing/LeadForm";
import { QuoteBuilder } from "@/components/landing/QuoteBuilder";
import { SectionLabel } from "@/components/site/FormBits";
import { useSeo, pageSeo, SEO_PAGES } from "@/lib/seo";
import { HeroBackground } from "@/components/landing/HeroBackground";
import { BookCallButton } from "@/components/site/BookCall";
import { UpcomingClass } from "@/pages/ClassPage";
import { AI_COURSE_OUTCOME } from "@/lib/constants";
import { RetreatDates } from "@/components/site/RetreatDates";
import { CommunityPromo } from "@/components/community/CommunityPromo";
import { RetreatBundle } from "@/components/avenues/AvenueParts";

const TEACH = [
  { icon: Globe, t: "Websites & digital products", d: "How a practical website or simple digital service can solve a clear business problem, shown with before-and-after walkthroughs." },
  { icon: Megaphone, t: "Marketing & SEO", d: "How marketing, local knowledge, and online systems make a business easier to discover and operate." },
  { icon: Briefcase, t: "Client work", d: "How to turn a skill into a narrow offer, explain the scope, show proof, and keep remote projects moving." },
  { icon: Workflow, t: "Online business systems", d: "Examples from projects such as K9OS, TrackMyPackage, and Ship Costa Rica: onboarding, records, handoffs, and clear updates." },
  { icon: MapPin, t: "Operating from Nicaragua", d: "Routines, relationships, logistics, and tradeoffs learned building and running projects while based in Nicaragua." },
];

const FAQ = SEO_PAGES["/"].faq;
const SEO = pageSeo("/");

const NEXT_STEPS = [
  "Tell me where you are and what you'd love to build — it takes about 3 minutes.",
  "Mr. Greg reads every inquiry personally and replies by email or WhatsApp.",
  "Together we pick the right path: the beginners course, the AI website course, or a collaboration.",
];

export default function Landing() {
  useSeo(SEO);
  return (
    <>
      <section className="relative overflow-clip" data-testid="landing-hero">
        <div className="absolute inset-0 bg-[#0B2A4A] grain overflow-hidden" aria-hidden>
          <HeroBackground />
          <div className="absolute inset-x-0 top-0 aspect-[3/4] md:aspect-auto md:h-[92vh] md:max-h-[860px] bg-gradient-to-r from-[#0B2A4A]/85 via-[#0B2A4A]/45 to-[#0B2A4A]/10" />
          <div className="absolute inset-x-0 top-[calc(133.34vw-200px)] h-[201px] md:top-[calc(min(92vh,860px)-240px)] md:h-[241px] bg-gradient-to-b from-transparent to-[#0B2A4A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-16 grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-14 items-start">
          <div className="text-[#F5ECD7] lg:sticky lg:top-28">
            <p className="rise font-mono-ui text-xs tracking-[0.35em] text-[#F08A3C]">WORK • EXPLORE • BELONG</p>
            <h1 className="rise rise-1 mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
              Digital Nomad Nicaragua: let's build your remote-work path.
            </h1>
            <h2 className="rise rise-2 mt-6 text-base md:text-lg text-[#F5ECD7]/85 max-w-lg leading-relaxed font-normal">
              Whether you're just dreaming about the nomad life or already running your own project — tell me where you are and what you'd love to build. If something clicks, we'll find a way to work together.
            </h2>
            <div className="rise rise-3 mt-8 flex flex-wrap gap-3">
              <Link to="/digital-nomad-beginners-course" className="inline-flex items-center gap-2 rounded-xl bg-[#1A9AA8] text-white px-5 py-3 font-semibold hover:bg-[#147C88]" data-testid="hero-course-cta">
                Become a Digital Nomad for Beginners Course <ArrowRight className="h-4 w-4" />
              </Link>
              <BookCallButton source="home-hero" light testId="hero-book-call-btn" />
            </div>
            <img src="/brand/greg-cover.jpg" alt="Greg Miceli — Digital Nomad Success: create your own opportunities, working from Nicaragua" className="rise rise-3 mt-10 w-full aspect-video object-cover rounded-2xl shadow-2xl ring-1 ring-white/20" data-testid="hero-cover-photo" />
            <div className="hidden lg:block mt-8 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur-md p-6" data-testid="hero-next-steps">
              <p className="font-mono-ui text-[11px] tracking-[0.3em] text-[#F08A3C]">WHAT HAPPENS NEXT</p>
              <ol className="mt-4 grid gap-3">
              {NEXT_STEPS.map((s, i) => (
                <li key={s} className="flex gap-3 items-start text-sm text-[#F5ECD7]/90">
                  <span className="shrink-0 h-6 w-6 rounded-full bg-[#1A9AA8] text-white text-xs font-bold grid place-items-center">{i + 1}</span>{s}
                </li>
              ))}
              </ol>
            </div>
          </div>
          <div className="rise rise-2 bg-white rounded-3xl shadow-2xl shadow-[#0B2A4A]/20 border border-[#0B2A4A]/10 p-6 sm:p-10" id="collaborate">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2A4A] tracking-tight">Let's Collaborate</h2>
            <p className="mt-2 mb-8 text-sm text-slate-600">Answer a few questions. Every inquiry goes straight to Mr. Greg and starts your student profile.</p>
            <LeadForm />
          </div>
        </div>
      </section>

      <UpcomingClass />
      <RetreatDates />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16" data-testid="landing-course-band">
        <div className="rounded-3xl bg-[#F5ECD7] border border-[#0B2A4A]/10 p-8 sm:p-12 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
          <div>
            <SectionLabel>Flagship course · 7 days · 60-minute lessons</SectionLabel>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2A4A]">Become a Digital Nomad for Beginners</h2>
            <p className="mt-4 text-slate-700 leading-relaxed max-w-xl">
              A host-led, seven-day beginner course, taught in person as a 7-day retreat at Esc Digital Nomad Village in Popoyo, Nicaragua. Attend 2 hours of live teaching each day, answer questions in class, work through your worksheets in your own time in the afternoon, and save every worksheet to your private student profile. Students apply first and lessons unlock after approval.
            </p>
            <p className="mt-4 font-semibold text-[#0B2A4A]" data-testid="landing-course-price">7-day retreat from $1,190 (twin room) or $1,470 (private room)</p>
            <p className="mt-1 text-sm text-slate-700" data-testid="landing-course-monthly">Or stay the month at Esc Digital Nomad Village: $3,295 twin · $3,750 private.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/digital-nomad-beginners-course" className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-7 py-4 font-semibold shadow-md hover:bg-[#D96F28]" data-testid="landing-course-cta">
              Become a Digital Nomad for Beginners Course <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <CommunityPromo />
        <div className="mt-5 rounded-3xl bg-white border border-[#0B2A4A]/10 p-6 sm:p-10 grid md:grid-cols-[minmax(0,340px)_1fr] gap-8 items-center" data-testid="landing-ai-course-band">
          <img src="/brand/ai-poster-b.jpg" alt="7-Day AI Web Developer Course — build your business website yourself with AI, HTML and React" className="w-full rounded-2xl shadow-xl" data-testid="landing-ai-course-image" />
          <div className="flex flex-col gap-6">
          <div>
            <SectionLabel>Course · Starting at $5,000</SectionLabel>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2A4A]">AI Website Web Developer Course</h2>
            <p className="mt-4 text-slate-700 leading-relaxed max-w-xl">Learn to plan, build, optimize, and launch professional websites with AI tools. Request more information and see the portfolio of real projects.</p>
            <p className="mt-3 font-semibold text-[#0B2A4A] max-w-xl" data-testid="landing-ai-outcome">{AI_COURSE_OUTCOME}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/ai-website-developer-course" className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-6 py-4 font-semibold hover:bg-[#071C33]" data-testid="landing-ai-course-cta">
              Learn more <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="https://gregmiceli.com/portfolio" target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-xl border border-[#1A9AA8] text-[#147C88] px-6 py-4 font-semibold hover:bg-[#1A9AA8]/10" data-testid="landing-portfolio-cta">View portfolio</a>
          </div>
          </div>
        </div>
      </section>

      <RetreatBundle testId="landing-avenues-bundle" showGrid={false} />


      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid lg:grid-cols-2 gap-12 items-center">
        <div className="grid grid-cols-5 gap-3">
          <img src="/brand/esc-aerial.jpg" alt="Aerial view of the Esc Digital Nomad Village above the Pacific coast in Popoyo, Nicaragua" className="col-span-5 rounded-2xl object-cover h-64 w-full" />
          <img src="/brand/esc-clubhouse.jpg" alt="Esc Digital Nomad Village clubhouse at dusk in Popoyo, Nicaragua" className="col-span-3 rounded-2xl object-cover h-44 w-full" />
          <div className="col-span-2 rounded-2xl bg-[#0B2A4A] text-[#F5ECD7] p-4 flex flex-col justify-end">
            <p className="font-mono-ui text-[10px] tracking-[0.25em] text-[#F08A3C]">POPOYO, NICARAGUA</p>
            <p className="font-display font-semibold mt-1">Esc Digital Nomad Village</p>
          </div>
        </div>
        <div>
          <SectionLabel>Remote work in Nicaragua</SectionLabel>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Learn from real projects built while living in Nicaragua</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Mr. Greg's path runs from Queens and Tampa Bay to San Juan del Sur. The lessons draw on hands-on work building websites, online services, business systems, and marketing — shared as examples you can learn from, not as promised results.
          </p>
          <ul className="mt-8 flex flex-col gap-5">
            {TEACH.map(({ icon: Icon, t, d }) => (
              <li key={t} className="flex gap-4">
                <span className="h-10 w-10 shrink-0 rounded-xl bg-[#1A9AA8]/10 text-[#147C88] flex items-center justify-center"><Icon className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-semibold text-[#0B2A4A]">{t}</h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-12" data-testid="landing-quote-section">
        <SectionLabel>Build your custom program</SectionLabel>
        <h2 className="mt-3 mb-8 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">AI Website Web Developer Course + add-ons</h2>
        <QuoteBuilder />
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-16" data-testid="landing-faq">
        <SectionLabel>Questions</SectionLabel>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">How to become a digital nomad — the honest version</h2>
        <div className="mt-8 divide-y divide-[#0B2A4A]/10 border-y border-[#0B2A4A]/10">
          {FAQ.map((f) => (
            <div key={f.q} className="py-6">
              <h3 className="font-semibold text-lg text-[#0B2A4A]">{f.q}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
